import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useParams, useNavigate, Link } from "react-router-dom";
import NavBar from "../components/Navigationbar";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbSeparator,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import {
  Send,
  MapPin,
  Users,
  Smile,
  BedDouble,
  Bath,
  ArrowLeft,
  CheckCircle,
} from "lucide-react";

import Footer from "../components/Footer";

const API_URL = import.meta.env.VITE_API_URL;

// same slugify logic as PropertyDetails
const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

function Enquire() {
  const { id } = useParams(); // we don't really need slug from params since we can regenerate it
  const location = useLocation();
  const navigate = useNavigate();

  const [property, setProperty] = useState(location.state?.property || null);
  const [loadingProperty, setLoadingProperty] = useState(
    !location.state?.property
  );

  const slug = property ? slugify(property.title || "property") : "";

  useEffect(() => {
    // If user refreshed or opened the link directly, location.state will be missing.
    if (location.state?.property) return;

    const fetchProperty = async () => {
      try {
        setLoadingProperty(true);

        // IMPORTANT: Use the SAME endpoint you use in your PropertyDetails page fetch.
        // Replace this URL if your actual endpoint is different.
        const res = await fetch(`${API_URL}/api/public/accommodations/${id}`);
        if (!res.ok) throw new Error("Failed to load property");

        const data = await res.json();

        // If your API returns { accommodation: {...} } then use data.accommodation instead.
        setProperty(data);
      } catch (err) {
        console.error("Failed to fetch property on refresh:", err);
        setProperty(null);
      } finally {
        setLoadingProperty(false);
      }
    };

    fetchProperty();
  }, [id, location.state, API_URL]);

  // -------- NEW: form state ----------
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [error, setError] = useState("");

  // -------- NEW: submit handler ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!property) return;

    // validation
    if (
      !formData.name.trim() ||
      !formData.email.trim() ||
      !formData.phone.trim() ||
      !formData.message.trim()
    ) {
      setError("Full name, email, phone number, and message are required.");
      return;
    }

    if (formData.phone.trim().length < 8) {
      setError("Please enter a valid phone number.");
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/public/inquiries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          accommodation_id: property.id, // ⬅ link to this property
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.message || "Failed to send enquiry");
      }

      // simple success handling – reset form
      setFormData({ name: "", email: "", phone: "", message: "" });
      setError("");
      setSuccessOpen(true);
    } catch (err) {
      console.error("Error sending enquiry:", err);
      setError(err.message || "Failed to send enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingProperty) {
    return (
      <div>
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p className="text-gray-700">Loading property details...</p>
        </div>
      </div>
    );
  }

  // Handle direct access / refresh where state is lost AND fetch failed
  if (!property) {
    return (
      <div>
        <NavBar />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <button
            onClick={() => navigate("/properties")}
            className="flex items-center gap-2 text-gray-600 hover:text-[#D2138C] transition-colors mb-4 cursor-pointer">
            <ArrowLeft className="w-5 h-5" />
            Back to properties
          </button>
          <p className="text-gray-700">
            We couldn’t load the property details. Please go back to the
            properties page and select a property again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <NavBar />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumbs */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/properties">Properties</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbPage>{property.title}</BreadcrumbPage>
            </BreadcrumbItem>

            <BreadcrumbSeparator />

            <BreadcrumbItem>
              <BreadcrumbPage>Enquire</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Accommodation Information */}
          <div className="p-8">
            {/* Title and Location */}
            <div className="flex items-center gap-1 mb-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4" />
              <span>{property.location}</span>
            </div>
            <h2 className="text-xl font-normal mb-2">{property.title}</h2>

            {/* Some Details */}
            <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-gray-700">
              <div className="flex items-center gap-1">
                <BedDouble className="w-3.5 h-3.5" />
                <span>{property.bedrooms} Bed</span>
              </div>
              <div className="flex items-center gap-1">
                <Bath className="w-3.5 h-3.5" />
                <span>{property.bathrooms} Bath</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                <span>
                  {property.capacity}{" "}
                  {property.capacity === 1 ? "person" : "people"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {property.gender === "Men & Women" ? (
                  <Users className="w-3.5 h-3.5" />
                ) : (
                  <Smile className="w-3.5 h-3.5" />
                )}
                <span>{property.gender}</span>
              </div>
            </div>

            {/* Description */}
            <div className="mb-4 text-gray-700">
              <p className="text-sm font-normal">{property.description}</p>
            </div>

            {/* View Accommodation details */}
            <Button
              variant="outline"
              className="cursor-pointer"
              onClick={() =>
                navigate(`/properties/${property.id}/${slug}`, {
                  state: { property },
                })
              }>
              <ArrowLeft className="h-4 w-4 mr-1" /> View accommodation details
            </Button>
          </div>

          {/* Enquire Form */}
          <div className="p-8">
            <h2 className="text-gray-900 text-xl font-normal mb-2">
              Enquire about this Property
            </h2>
            <p className="text-gray-600 text-sm mb-6">
              Tell us a little about what you need and we'll get back to you
              shortly.
            </p>

            {/* ⬇⬇ added onSubmit + controlled fields */}
            <form className="space-y-4" onSubmit={handleSubmit}>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Enter your name"
                  required
                  className="mt-1"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter your email"
                  required
                  className="mt-1"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Enter your phone number"
                  required
                  className="mt-1"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                />
              </div>

              <div>
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Type your message or questions here..."
                  rows={5}
                  required
                  className="mt-1"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      message: e.target.value,
                    }))
                  }
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-[#D2138C] hover:bg-[#D2138C] text-white rounded-xl cursor-pointer"
                disabled={submitting}>
                <Send className="w-4 h-4 mr-2" />
                {submitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>

      <Footer />

      {successOpen &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
                <CheckCircle className="h-7 w-7 text-green-600" />
              </div>

              <h3 className="text-lg font-semibold mb-1">
                Your enquiry has been sent
              </h3>
              <p className="text-sm text-gray-600 mb-5">
                Thank you for reaching out. We&apos;ll get back to you shortly.
              </p>

              <Button
                className="bg-[#D2138C] hover:bg-pink-700 rounded-full text-white px-6"
                onClick={() => setSuccessOpen(false)}>
                OK
              </Button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default Enquire;
